import React, { useState } from 'react'
import { useQuery } from '@apollo/client'
import { get } from 'lodash'
import { DateTime } from 'luxon'

import LinearProgress from '@material-ui/core/LinearProgress'
import Box from '@material-ui/core/Box'
import EventNoteOutlinedIcon from 'components/material-symbol/icons/EventNote'
import SectionEmptyState from 'components/section-empty-state'
import { TonalButton } from 'components/empty-page-state'

import { GetMyLessons } from './query'
import { NextSessionCard } from './session-card'
import SessionDetailModal from './session-detail'
import SessionSectionHeader from './session-section-header'
import {
  flattenUpcomingLessons,
  hasNoUpcomingLessons,
  parseLessons,
} from './utils'
import { useStyles } from './styles'

const UpcomingLessons = ({
  lessons,
  refetchLessons,
  onBookClick,
  embedded = false,
}) => {
  const classes = useStyles()
  const [openLesson, setOpenLesson] = useState({})
  const upcomingLessons = flattenUpcomingLessons(lessons)
  const isEmpty = hasNoUpcomingLessons(lessons)

  if (isEmpty) {
    return (
      <Box className={classes.emptyStateContent}>
        <SectionEmptyState
          Icon={EventNoteOutlinedIcon}
          title="No upcoming sessions"
          caption="When you book a lesson with your coach, it will appear here."
          bordered={false}
          action={
            onBookClick ? (
              <TonalButton
                onClick={onBookClick}
                startIcon={<EventNoteOutlinedIcon fontSize="small" />}
              >
                Book Session
              </TonalButton>
            ) : null
          }
        />
      </Box>
    )
  }

  return (
    <>
      {!embedded ? <SessionSectionHeader title="Upcoming sessions" /> : null}
      <Box className={classes.sessionList}>
        {upcomingLessons.map(lesson => (
          <NextSessionCard
            key={lesson.id}
            lesson={lesson}
            onOpenLesson={setOpenLesson}
          />
        ))}
      </Box>
      <SessionDetailModal
        openLesson={openLesson}
        setOpenLesson={setOpenLesson}
        refetchLessons={refetchLessons}
      />
    </>
  )
}

export default ({ studentId, onBookClick, embedded = false }) => {
  const { data, loading, error, refetch } = useQuery(GetMyLessons, {
    variables: {
      studentId,
      orderBy: {
        field: 'START_DATE_TIME',
        direction: 'ASC',
      },
      startDateTime: {
        from: DateTime.local()
          .startOf('minute')
          .toISO(),
      },
    },
  })

  if (loading && !data) return <LinearProgress color="secondary" />
  if (error) return <Box>ERROR</Box>

  const lessons = get(data, 'currentUser.lessons')
  const parsedLessons = parseLessons(lessons, studentId)

  return (
    <UpcomingLessons
      lessons={parsedLessons}
      refetchLessons={refetch}
      onBookClick={onBookClick}
      embedded={embedded}
    />
  )
}
